/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type ChangeEvent,
  type FC,
  type FocusEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Button,
  NavBar,
  NavItem,
  TooltipProvider,
} from "@vector-im/compound-web";

import styles from "./ColorThemePickerDialog.module.css";
import {
  type ColorThemePickerItemData,
  allColorThemeVars,
  cpdColorGroups,
  themeUtilityGroups,
} from "./colorThemePickerData";

type AllowedTheme = "light" | "dark";
type DialogPosition = "right" | "left" | "top-right" | "top-left";
type ColorValues = Record<string, string>;
type ColorValuesActionItem = { var: string; prev: string; next: string };
type ColorValuesData = {
  values: ColorValues;
  history: ColorValuesActionItem[];
  future: ColorValuesActionItem[];
  initial: ColorValues;
};
type SaveFileData = {
  changed: ColorValues;
  data: ColorValuesData;
  theme: AllowedTheme;
  datetime: string;
};

const LS_KEY_PREFIX = "__ColorThemePickerValues";

const readActiveTheme = (): AllowedTheme | null => {
  if (
    document.body.classList.contains("cpd-theme-light") ||
    document.body.classList.contains("cpd-theme-light-hc")
  ) {
    return "light";
  }

  if (
    document.body.classList.contains("cpd-theme-dark") ||
    document.body.classList.contains("cpd-theme-dark-hc")
  ) {
    return "dark";
  }

  return null;
};

const clearInlineThemeVars = (): void => {
  for (const item of allColorThemeVars) {
    document.body.style.removeProperty(item.var);
  }
};

const generateColorValues = (theme: AllowedTheme): ColorValues => {
  clearInlineThemeVars();
  const computedStyles = getComputedStyle(document.body);
  const vars = [
    ...Object.values(cpdColorGroups).flat(),
    ...themeUtilityGroups[theme],
  ];

  return Object.fromEntries(
    vars.map((item) => [
      item.var,
      item.defaultValue ?? computedStyles.getPropertyValue(item.var).trim(),
    ]),
  );
};

const saveColorValuesIntoLocalStorage = (
  theme: AllowedTheme,
  data: ColorValuesData,
): void => {
  localStorage.setItem(`${LS_KEY_PREFIX}-${theme}`, JSON.stringify(data));
};

const getInitialColorValues = (theme: AllowedTheme): ColorValuesData => {
  const stored = localStorage.getItem(`${LS_KEY_PREFIX}-${theme}`);
  if (stored) {
    return JSON.parse(stored) as ColorValuesData;
  }

  const values = generateColorValues(theme);
  const data: ColorValuesData = {
    values,
    initial: { ...values },
    history: [],
    future: [],
  };
  saveColorValuesIntoLocalStorage(theme, data);
  return data;
};

const exportData = (theme: AllowedTheme): void => {
  const data = getInitialColorValues(theme);
  const changed = Object.fromEntries(
    Object.entries(data.values).filter(
      ([name, value]) => value !== data.initial[name],
    ),
  );

  const payload: SaveFileData = {
    theme,
    datetime: new Date().toLocaleString(),
    changed,
    data,
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const anchor = document.createElement("a");
  anchor.href = URL.createObjectURL(blob);
  anchor.download = `${theme}-${Date.now()}.json`;
  anchor.click();
  URL.revokeObjectURL(anchor.href);
};

const importData = async (theme: AllowedTheme): Promise<ColorValues | null> => {
  return await new Promise((resolve) => {
    const input = document.createElement("input");
    let settled = false;

    const finish = (value: ColorValues | null): void => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };

    input.type = "file";
    input.accept = "application/json";
    input.addEventListener("cancel", () => finish(null), { once: true });
    input.onchange = async (): Promise<void> => {
      const file = input.files?.[0];
      if (!file) {
        finish(null);
        return;
      }

      try {
        const text = await file.text();
        const json = JSON.parse(text) as SaveFileData;
        if (json.theme !== theme) {
          console.warn(
            `ColorThemePickerDialog: imported theme "${json.theme}" does not match current theme "${theme}"`,
          );
          finish(null);
          return;
        }
        finish(json.changed);
      } catch {
        finish(null);
      }
    };
    input.click();
  });
};

const useColorsData = (theme: AllowedTheme | null) => {
  const [data, setData] = useState<ColorValuesData | null>(null);

  useEffect(() => {
    if (!theme) {
      setData(null);
      clearInlineThemeVars();
      return;
    }

    setData(getInitialColorValues(theme));
  }, [theme]);

  useEffect(() => {
    if (!data) {
      clearInlineThemeVars();
      return;
    }

    clearInlineThemeVars();
    for (const [name, value] of Object.entries(data.values)) {
      if (value !== data.initial[name]) {
        document.body.style.setProperty(name, value);
      }
    }

    if (theme) {
      saveColorValuesIntoLocalStorage(theme, data);
    }
  }, [data, theme]);

  return {
    data,
    canBack: !!data && data.history.length > 0,
    canForward: !!data && data.future.length > 0,
    handlers: {
      setValue: (varName: string, value: string): void => {
        setData((prev) => {
          if (!prev) return prev;
          const prevValue = prev.values[varName];
          if (prevValue === value) return prev;
          return {
            values: { ...prev.values, [varName]: value },
            initial: prev.initial,
            history: [
              ...prev.history,
              { var: varName, prev: prevValue, next: value },
            ].slice(-30),
            future: [],
          };
        });
      },
      stepBack: (): void => {
        setData((prev) => {
          if (!prev || prev.history.length === 0) return prev;
          const lastAction = prev.history.at(-1)!;
          return {
            values: { ...prev.values, [lastAction.var]: lastAction.prev },
            history: prev.history.slice(0, -1),
            future: [lastAction, ...prev.future],
            initial: prev.initial,
          };
        });
      },
      stepForward: (): void => {
        setData((prev) => {
          if (!prev || prev.future.length === 0) return prev;
          const nextAction = prev.future[0];
          return {
            values: { ...prev.values, [nextAction.var]: nextAction.next },
            history: [...prev.history, nextAction],
            future: prev.future.slice(1),
            initial: prev.initial,
          };
        });
      },
      reset: (): void => {
        if (!theme) return;
        localStorage.removeItem(`${LS_KEY_PREFIX}-${theme}`);
        setData(getInitialColorValues(theme));
      },
      export: (): void => {
        if (!theme) return;
        exportData(theme);
      },
      import: async (): Promise<void> => {
        if (!theme) return;
        const changed = await importData(theme);
        if (!changed) return;
        const base = getInitialColorValues(theme);
        setData({
          ...base,
          values: { ...base.values, ...changed },
        });
      },
    },
  };
};

const ColorVarItem: FC<{
  data: ColorThemePickerItemData;
  isDefault: boolean;
  value: string;
  onChange: (value: string) => void;
}> = ({ data, isDefault, value, onChange }) => {
  const inputId = useId();
  const draftValueRef = useRef<string | null>(null);
  const [draftValue, setDraftValue] = useState(value);
  const title = <p className={styles.itemName}>{`${data.name}${isDefault ? "" : "*"}`}</p>;

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  const commitDraftValue = (): void => {
    if (draftValueRef.current === null) return;
    onChange(draftValueRef.current);
    draftValueRef.current = null;
  };

  return (
    <div className={`${styles.item} ${isDefault ? "" : styles.itemChanged}`}>
      {data.inputType === "text" ? (
        <span
          className={styles.pickerSwatch}
          style={{ backgroundColor: `var(${data.var})` }}
        />
      ) : (
        <label className={styles.pickerLabel}>
          <input
            className={styles.pickerInput}
            type="color"
            onFocus={(event: FocusEvent<HTMLInputElement>) => {
              draftValueRef.current = event.target.value;
            }}
            onInput={(event: ChangeEvent<HTMLInputElement>) => {
              draftValueRef.current = event.target.value;
              document.body.style.setProperty(data.var, event.target.value);
            }}
            onBlur={commitDraftValue}
          />
          <span
            className={styles.pickerSwatch}
            style={{ backgroundColor: `var(${data.var})` }}
          />
        </label>
      )}
      <div className={styles.itemMeta}>
        {title}
        <p className={styles.itemVar}>{data.var}</p>
        {data.description && (
          <p className={styles.itemDescription}>{data.description}</p>
        )}
        {data.inputType === "text" && (
          <input
            id={inputId}
            className={styles.valueInput}
            type="text"
            value={draftValue}
            onFocus={() => {
              draftValueRef.current = draftValue;
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              draftValueRef.current = event.target.value;
              setDraftValue(event.target.value);
              document.body.style.setProperty(data.var, event.target.value);
            }}
            onBlur={commitDraftValue}
          />
        )}
      </div>
    </div>
  );
};

const ColorVarsBlock: FC<{
  title: string;
  vars: readonly ColorThemePickerItemData[];
  colorsData: ReturnType<typeof useColorsData>;
}> = ({ title, vars, colorsData }) => {
  if (!colorsData.data) return null;

  return (
    <section className={styles.block}>
      <p className={styles.blockTitle}>{title}</p>
      {vars.map((item) => {
        const value = colorsData.data!.values[item.var];
        const isDefault = value === colorsData.data!.initial[item.var];
        return (
          <ColorVarItem
            key={item.var}
            data={item}
            isDefault={isDefault}
            value={value}
            onChange={(value) => colorsData.handlers.setValue(item.var, value)}
          />
        );
      })}
    </section>
  );
};

export const ColorThemePickerDialog: FC = () => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<DialogPosition>("right");
  const [activePanelId, setActivePanelId] = useState<"cpd" | "utility">("cpd");
  const [theme, setTheme] = useState<AllowedTheme | null>(() => readActiveTheme());
  const colorsData = useColorsData(theme);

  useEffect(() => {
    const syncTheme = (): void => {
      setTheme(readActiveTheme());
    };

    syncTheme();

    const observer = new MutationObserver(syncTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return (): void => observer.disconnect();
  }, []);

  useEffect(() => {
    const positions: Record<DialogPosition, Record<string, DialogPosition>> = {
      right: {
        ArrowLeft: "left",
        ArrowUp: "top-right",
      },
      left: {
        ArrowRight: "right",
        ArrowUp: "top-left",
      },
      "top-right": {
        ArrowLeft: "top-left",
        ArrowDown: "right",
      },
      "top-left": {
        ArrowRight: "top-right",
        ArrowDown: "left",
      },
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }

      if (
        event.ctrlKey &&
        event.altKey &&
        !event.shiftKey &&
        !event.metaKey &&
        (event.code === "KeyD" || event.key.toLowerCase() === "d")
      ) {
        event.preventDefault();
        setVisible((prev) => !prev);
        return;
      }

      if (
        event.ctrlKey &&
        event.altKey &&
        !event.shiftKey &&
        !event.metaKey &&
        event.key in positions[position]
      ) {
        event.preventDefault();
        setPosition(positions[position][event.key]);
        return;
      }

      if (event.ctrlKey && event.shiftKey && !event.altKey && !event.metaKey) {
        if (event.key === "ArrowLeft") {
          colorsData.handlers.stepBack();
        } else if (event.key === "ArrowRight") {
          colorsData.handlers.stepForward();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return (): void => window.removeEventListener("keydown", onKeyDown);
  }, [colorsData.handlers, position]);

  const utilityVars = theme ? themeUtilityGroups[theme] : [];

  const content: ReactNode = (
    <TooltipProvider>
      <div
        className={`${styles.dialog} ${position.includes("left") ? styles.left : ""} ${
          position.includes("top") ? styles.top : ""
        }`}
      >
        <NavBar className={styles.nav} aria-label="color-theme-picker">
          <NavItem
            active={activePanelId === "cpd"}
            onClick={() => setActivePanelId("cpd")}
          >
            cpd
          </NavItem>
          <NavItem
            active={activePanelId === "utility"}
            onClick={() => setActivePanelId("utility")}
          >
            utility
          </NavItem>
        </NavBar>
        <div
          className={`${styles.body} ${
            activePanelId === "utility" ? styles.singleColumn : ""
          }`}
        >
          {activePanelId === "cpd" && (
            <>
              <ColorVarsBlock
                title="text"
                vars={cpdColorGroups.text}
                colorsData={colorsData}
              />
              <ColorVarsBlock
                title="bg"
                vars={cpdColorGroups.bg}
                colorsData={colorsData}
              />
              <ColorVarsBlock
                title="border"
                vars={cpdColorGroups.border}
                colorsData={colorsData}
              />
              <ColorVarsBlock
                title="icon"
                vars={cpdColorGroups.icon}
                colorsData={colorsData}
              />
              <ColorVarsBlock
                title="palette"
                vars={cpdColorGroups.palette}
                colorsData={colorsData}
              />
            </>
          )}
          {activePanelId === "utility" && (
            <ColorVarsBlock
              title={theme ?? "utility"}
              vars={utilityVars}
              colorsData={colorsData}
            />
          )}
        </div>
        <div className={styles.buttons}>
          <div className={styles.buttonGroup}>
            <Button
              kind="secondary"
              size="sm"
              onClick={colorsData.handlers.reset}
            >
              Сброс
            </Button>
            <Button
              kind="secondary"
              size="sm"
              onClick={(): void => {
                void colorsData.handlers.import();
              }}
            >
              Импорт
            </Button>
            <Button size="sm" onClick={colorsData.handlers.export}>
              Экспорт
            </Button>
          </div>
          <div className={styles.buttonGroup}>
            <Button
              kind="secondary"
              size="sm"
              disabled={!colorsData.canBack}
              onClick={colorsData.handlers.stepBack}
            >
              Undo
            </Button>
            <Button
              kind="secondary"
              size="sm"
              disabled={!colorsData.canForward}
              onClick={colorsData.handlers.stepForward}
            >
              Redo
            </Button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );

  return visible ? createPortal(content, document.body) : null;
};
